/**
 * Thin client for the standalone TAS STT FastAPI service.
 * The service URL is configured at build time via VITE_STT_API_URL.
 * No auth header — the service is currently open. If/when it gains auth,
 * attach the Firebase ID token here.
 */

const BASE = import.meta.env.VITE_STT_API_URL ?? '';

export class STTError extends Error {
  constructor(public readonly status: number, message: string) {
    super(message);
    this.name = 'STTError';
  }
}

export type Template = 'going_concern' | 'liquidation';

export interface TranscribeStartResponse {
  task_id: string;
  status: string;
  files?: number;
}

export interface Correction {
  original: string;
  corrected: string;
  source?: string;
}

export interface ProgressStats {
  total_items?: number;
  total_headers?: number;
  total_value?: number;
  leasehold_items?: number;
  leasehold_value?: number;
  unknown_brands?: number;
  value_warnings?: number;
}

export interface Progress {
  task_id: string;
  status: string;
  progress: number;
  stage: string;
  filename?: string;
  stats?: ProgressStats;
  corrections?: Correction[];
  error?: string;
}

export function sttConfigured(): boolean {
  return BASE.length > 0;
}

export function sttBaseUrl(): string {
  return BASE;
}

// Only the v2 pipeline (ElevenLabs Scribe + Claude) is exposed in the portal.
// The "classic" Node.js pipeline stays at assets.uraniumgames.gg as Greg's
// legacy fallback; we don't proxy it here.
const DONE_STATUS = 'complete';

export async function startTranscription(
  template: Template,
  files: File[],
): Promise<TranscribeStartResponse> {
  const form = new FormData();
  for (const f of files) form.append('files', f);
  const url = `${BASE}/v2/transcribe?template=${encodeURIComponent(template)}`;
  const res = await fetch(url, { method: 'POST', body: form });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new STTError(res.status, `Upload failed (${res.status}): ${text.slice(0, 200)}`);
  }
  return res.json();
}

export async function pollProgress(taskId: string): Promise<Progress> {
  const res = await fetch(`${BASE}/v2/progress/${taskId}`);
  if (!res.ok) throw new STTError(res.status, `Progress check failed: ${res.status}`);
  return res.json();
}

export async function downloadExcel(taskId: string): Promise<Blob> {
  const res = await fetch(`${BASE}/v2/download/${taskId}`);
  if (!res.ok) throw new STTError(res.status, `Download failed: ${res.status}`);
  return res.blob();
}

export function isComplete(status: string): boolean {
  return status === DONE_STATUS;
}
